import React from "react";
import {
  type GridColDef,
  type GridRenderCellParams,
  type GridValueFormatterParams,
  type GridValueGetterParams,
} from "@mui/x-data-grid-pro";
import { DownloadCell } from "@/features/propertyCodeAcquisition/components/DownloadCell";
import { PictureReceivedDateCell } from "@/features/propertyCodeAcquisition/components/PictureReceivedDateCell";
import {
  getPrimaryPictureReceivedDate,
  safeFormatIsoDate,
} from "@/features/propertyCodeAcquisition/utils/pictureReceivedDate";
import {
  PICTURE_CELL_FIELD_LABELS,
  type PictureCellField,
} from "@/features/propertyCodeAcquisition/utils/pictureCellFields";
import type {
  HistoryPictureCell,
  PropertyInfoResponse,
  RequestStatusCode,
  PropertyCodePictureRequestRow,
} from "@/features/propertyCodeAcquisition/types";

/** DataGrid 行（PropertyCodePictureRequestRow に DataGrid 必須の id を付与）。 */
export type PropertyCodePictureRequestGridRow =
  PropertyCodePictureRequestRow & { id: number };

/** 依頼ステータスの表示文言。色のみに依存しない。 */
const STATUS_LABEL: Record<RequestStatusCode, string> = {
  SUCCESS: "取得完了",
  REQUESTING: "依頼中",
  SOME_FAILED: "一部失敗",
  FAILED: "失敗",
};

/** ステータス列の並び順（昇順 = 取得完了→一部失敗→失敗→依頼中）。 */
const STATUS_SORT_RANK: Record<RequestStatusCode, number> = {
  SUCCESS: 0,
  SOME_FAILED: 1,
  FAILED: 2,
  REQUESTING: 3,
};

/** 未知ステータス（BE 追加等）は昇順で既知の後ろに来る序数にする。 */
const statusSortRank = (value: RequestStatusCode | null | undefined): number =>
  value != null ? STATUS_SORT_RANK[value] ?? 99 : 99;

/**
 * 登記・図面の DL セル列を生成する。zenbujiko/shoyushajiko/図面系の全種別で同構造を共有する
 * （DownloadCell に uri 連動の活性制御・uri 直開きを委譲）。
 */
const makeDownloadColumn = (
  field: PictureCellField,
  headerName: string
): GridColDef => ({
  field,
  headerName,
  width: 120,
  sortable: false,
  align: "center",
  headerAlign: "center",
  valueFormatter: () => "",
  renderCell: ({
    value,
  }: GridRenderCellParams<
    HistoryPictureCell,
    PropertyCodePictureRequestGridRow
  >) => <DownloadCell cell={value} label={`${headerName}をダウンロード`} />,
});

/**
 * 取得履歴テーブルの列定義を生成する（makeGridColDef パターン）。
 * - 列: 登記取得依頼日 / 登記取得日 / ステータス / 全部事項 / 所有者事項 / 電子公図 /
 *   地積測量図 / 地役権図面 / 建物図面
 * - 並べ替え: 日時・ステータス列はクライアントソート可能（BE 全件返却のため全件に効く）。
 *   初期 sortModel は設定せずサーバ降順の並びを既定とし、ユーザー操作時のみ上書きする
 * - DL 列（種別セル）はソート不可（makeDownloadColumn 側で sortable: false）。
 *   登記・図面セルは DownloadCell で uri 連動の活性制御・uri 直開きを行う
 * - 建物図面列の表示可否（土地で非表示）は columnVisibilityModel 側で制御する
 *   （makeHistoryColumnVisibilityModel）
 */
export const makeHistoryGridColDef = (): GridColDef[] => {
  return [
    {
      field: "requestedAt",
      headerName: "登記取得依頼日",
      width: 180,
      sortable: true,
      align: "center",
      headerAlign: "center",
      // ISO8601（JST, +09:00）を date-fns で整形。不正値は throw させず空表示にする。
      valueFormatter: (params: GridValueFormatterParams<string>) =>
        safeFormatIsoDate(params.value, "yyyy/MM/dd HH:mm", ""),
    },
    {
      // 登記取得日（法務局から受領した日＝登記の鮮度）。リユース取得では依頼日より古くなりうる。
      // 列名は DataLens 画面の表記に合わせている（他画面における「登記 / 図面依頼日」）。
      field: "pictureReceivedDate",
      headerName: "登記取得日",
      width: 120,
      sortable: true,
      align: "center",
      headerAlign: "center",
      // 行に無い派生値は valueGetter で供給する（クリップボードコピー等にも正しい値が乗る）。
      // row は本構成（集計/skeleton/グルーピング未使用）では常に存在するが、将来それらを
      // 導入した際に row 未定義で全画面クラッシュしないようガードしておく。
      valueGetter: ({
        row,
      }: GridValueGetterParams<unknown, PropertyCodePictureRequestGridRow>) =>
        row ? getPrimaryPictureReceivedDate(row) : null,
      renderCell: ({
        row,
        hasFocus,
      }: GridRenderCellParams<
        string | null,
        PropertyCodePictureRequestGridRow
      >) =>
        row ? <PictureReceivedDateCell row={row} hasFocus={hasFocus} /> : null,
    },
    {
      field: "status",
      headerName: "ステータス",
      width: 120,
      sortable: true,
      align: "center",
      headerAlign: "center",
      // renderCell/クリップボードには生コードを残したいため valueGetter ではなく sortComparator で意味順にする。
      sortComparator: (v1, v2) =>
        statusSortRank(v1 as RequestStatusCode) -
        statusSortRank(v2 as RequestStatusCode),
      renderCell: ({
        value,
      }: GridRenderCellParams<
        RequestStatusCode,
        PropertyCodePictureRequestGridRow
      >) =>
        // 未知ステータスは生値フォールバックで表示する。
        value != null ? STATUS_LABEL[value] ?? value : "",
    },
    // 種別セル列は共有の (フィールド, 表示名) 対から生成する（pictureReceivedDate と一元化）。
    ...PICTURE_CELL_FIELD_LABELS.map(([field, label]) =>
      makeDownloadColumn(field, label)
    ),
  ];
};

/**
 * 建物図面列の表示可否を表す columnVisibilityModel を生成する。
 * 建物図面は土地物件では常に exists=false のため列ごと非表示にする。
 * @param realEstateType 物件種別。"土地" のとき建物図面列を非表示にする。
 */
export const makeHistoryColumnVisibilityModel = (
  realEstateType?: PropertyInfoResponse["realEstateType"]
): Record<string, boolean> => ({
  tatemonozumen: realEstateType !== "土地",
});
